import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/apiClient";
import Scanner from "../components/Scanner";
import OrderList from "../components/OrderList";
import CustomerForm from "../components/CustomerForm";
import "../components/App.css";
import "./AppContent.css";

function AppContent() {
    const [manualEanInput, setManualEanInput] = useState('');
    const [scanQueue, setScanQueue] = useState([]);
    const [orderItems, setOrderItems] = useState([]);
    const [customerInfo, setCustomerInfo] = useState({
        full_name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        address2: '',
        zip_code: '',
        city: '',
        note: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState('');
    const [scanActive, setScanActive] = useState(false);
    const [cameraAvailable, setCameraAvailable] = useState(false);

    // Laster inn handlekurv fra localStorage ved oppstart
    useEffect(() => {
        const savedCart = localStorage.getItem("cart");
        if (savedCart) {
            setOrderItems(JSON.parse(savedCart));
        }
    }, []);

    // Oppdaterer localStorage når handlekurven endres
    useEffect(() => {
        localStorage.setItem("cart", JSON.stringify(orderItems));
    }, [orderItems]);

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const videoDevices = devices.filter(device => device.kind === "videoinput");
                setCameraAvailable(videoDevices.length > 0);
                setScanActive(videoDevices.length > 0);
            })
            .catch(err => {
                console.error("Feil ved detektering av kamera:", err);
                setCameraAvailable(false);
            });
    }, []);

    const fetchProductByEan = useCallback(async (ean) => {
        if (!ean) return;

        console.log(`Henter produkt for EAN: ${ean}`);
        setIsLoading(true);
        setError(null);
        setScanActive(false);

        try {
            const token = localStorage.getItem('authToken');
            if (!token) throw new Error('Ingen autentiseringstoken funnet. Vennligst logg inn.');

            const response = await apiClient.get('api/products/lookup/', {
                params: { ean },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const product = response.data;
            console.log("Produktdata:", product);

            setOrderItems((prevItems) => {
                const existingItemIndex = prevItems.findIndex(item => item.ean === product.ean);
                if (existingItemIndex > -1) {
                    const updatedItems = [...prevItems];
                    updatedItems[existingItemIndex].quantity += 1;
                    return updatedItems;
                } else {
                    return [...prevItems, { ...product, quantity: 1 }];
                }
            });

            setNotification(`Produkt med EAN ${ean} lagt til.`);
        } catch (err) {
            console.error("Feil ved henting av produkt:", err);
            if (err.response) {
                setError(err.response.data.detail || `Produkt ikke funnet for EAN: ${ean}`);
            } else {
                setError('Klarte ikke å kontakte serveren.');
            }
        } finally {
            setIsLoading(false);
            setScanActive(cameraAvailable);
        }
    }, [cameraAvailable]);

    useEffect(() => {
        if (scanQueue.length > 0 && !isLoading) {
            const nextEan = scanQueue[0];
            setScanQueue(prevQueue => prevQueue.slice(1));
            fetchProductByEan(nextEan);
        }
    }, [scanQueue, isLoading, fetchProductByEan]);

    const handleScanSuccess = (ean) => {
        if (ean && scanActive) {
            console.log("EAN skannet:", ean);
            setScanQueue(prevQueue => [...prevQueue, ean]);
        }
    };

    const handleCustomerInfoChange = (event) => {
        const { name, value } = event.target;
        setCustomerInfo(prevInfo => ({
            ...prevInfo,
            [name]: value,
        }));
    };

    const updateQuantity = (ean, newQuantity) => {
        setOrderItems(prevItems =>
            prevItems
                .map(item =>
                    item.ean === ean ? { ...item, quantity: Math.max(0, newQuantity) } : item
                )
                .filter(item => item.quantity > 0)
        );
    };

    const removeItem = (ean) => {
        setOrderItems(prevItems => prevItems.filter(item => item.ean !== ean));
    };

    const submitOrder = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const payload = {
                customer_info: customerInfo,
                products: orderItems,
            };

            await apiClient.post('api/orders/add-products/', payload);

            setNotification('Bestilling sendt inn!');
            setOrderItems([]);
            setCustomerInfo({
                full_name: '', email: '', phone: '', company: '',
                address: '', address2: '', zip_code: '', city: '', note: '',
            });

            // Tøm lagret handlekurv fra localStorage
            localStorage.removeItem("cart");
        } catch (err) {
            console.error("Feil:", err);
            setError('Klarte ikke å sende inn bestilling.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="app-container">
            <h1 className="app-title">EAN Bestillingsskanner</h1>

            <div className="scanner-section">
                <h2>Skann eller tast inn EAN</h2>
                {cameraAvailable && scanActive ? (
                    <Scanner
                        onScanSuccess={handleScanSuccess}
                        onScanError={(err) => console.error("Scanner-feil:", err)}
                    />
                ) : (
                    <p className="camera-error">Kamera ikke tilgjengelig. Bruk tekstfeltet nedenfor.</p>
                )}
                <div className="manual-input">
                    <input
                        type="text"
                        value={manualEanInput}
                        onChange={(e) => setManualEanInput(e.target.value)}
                        placeholder="Skriv EAN manuelt"
                        disabled={isLoading}
                    />
                    <button
                        className="add-item-button"
                        onClick={() => fetchProductByEan(manualEanInput.trim())}
                        disabled={!manualEanInput.trim() || isLoading}
                    >
                        Legg til
                    </button>
                </div>
                {notification && <p className="notification">{notification}</p>}
                {error && <p className="error-message">{error}</p>}
            </div>

            <div className="order-list-section">
                <OrderList
                    items={orderItems}
                    onUpdateQuantity={updateQuantity}
                    onRemoveItem={removeItem}
                />
            </div>

            <div className="customer-form-section">
                <CustomerForm
                    customerInfo={customerInfo}
                    onChange={handleCustomerInfoChange}
                />
            </div>

            <button
                className="submit-button"
                onClick={submitOrder}
                disabled={orderItems.length === 0 || isLoading}
            >
                Send bestilling
            </button>
        </div>
    );
}

export default AppContent;